import React, { useEffect, useRef } from 'react';
import { ReactComponent as CloudIcon } from '../../assets/icons/cloud.svg';
import { getDatesFromPeriod, unixToDate } from '../../shared/lib/dateUtils';
import { useMobile } from '../../contexts/MobileContext';
import { useDropdown } from '../../contexts/DropdownContext';
import ScrollingText from '../../shared/components/ScrollingText';
import { useMessage } from '../../contexts/MessageContext';

const MostRecentActivity = ({site}) => {
  let showMessage = useMessage();
  const { isMobile } = useMobile();
  let [data, setData] = React.useState([]);
  const { getSelectedOption } = useDropdown();
  const [percentage, setPercentage] = React.useState(null);
  const pollTimeoutRef = useRef(null);
  const isPollingRef = useRef(false);

  useEffect(() => {
    return () => {
      isPollingRef.current = false;
      if (pollTimeoutRef.current) {
        window.clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const pollPercentage = () => {
    if (!isPollingRef.current) return;

    fetch(process.env.REACT_APP_SERVER_URL + '/api/get-activity-percentage', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('token'),
      },
    })
      .then(response => response.json())
      .then(data => {
        if (!isPollingRef.current) return;
        setPercentage(data.percentage);

        if (data.percentage < 100) {
          // Note: this done because of recursive polling causing runaway requests and stale updates after leaving the page.
          pollTimeoutRef.current = window.setTimeout(pollPercentage, 1000);
        }
        else {
          isPollingRef.current = false;
          setPercentage(null);
        }
      })
      .catch(err => {
        console.error('Polling error:', err);
        if (!isPollingRef.current) return;
        // Note: this done because of recursive polling causing runaway requests and stale updates after leaving the page.
        pollTimeoutRef.current = window.setTimeout(pollPercentage, 1000);
      });
  }

  const formatDate = (date) => {
    const day = date.getDate();
    const year = date.getFullYear();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = monthNames[date.getMonth()];
    return `${day}_${month}_${year}`;
  }

  const handleDownload = () => {
    let option = getSelectedOption('period');
    let { start, end } = getDatesFromPeriod(option);

    fetch(process.env.REACT_APP_SERVER_URL + '/api/get-activity', {
      method: 'POST',
      headers: {
        Authorization: localStorage.getItem('token'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        start: start,
        end: end,
        domain: site.website,
      }),
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        let filename = site.website + '_' +
            formatDate(start) + '_to_' +
            formatDate(end) + '.csv';

        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        showMessage(`Saved data to file ${filename}.`);
      })
      .catch(error => {
        console.error('Download failed:', error);
      });

    isPollingRef.current = true;
    if (pollTimeoutRef.current) {
      window.clearTimeout(pollTimeoutRef.current);
    }
    setPercentage(0);
    pollPercentage();
  };

  useEffect(() => {
    let result = [];

    for (let entries of site.data) {
      for (let entry of Object.values(entries)) {
        let as = entry[4];
        for (let activity of as) {
          result.push(activity);
        }
      }
    }

    result = result.sort((a, b) => b['Date/Time (Unix)'] - a['Date/Time (Unix)']);
    result = result.slice(0, 5);

    for (let i = 0; i < result.length; i++) {
      if (result[i].Source.toLowerCase() === 'na') {
        result[i].Source = 'Direct';
      }
    }

    setData(result);
  }, [site]);

  return (
    <>
      <div className="section-header__with-buttons">
        <h4 className="section-title">Most recent bot activity</h4>

        {data.length > 0 && (
          <button
            type="button"
            className="btn secondary activity-download"
            onClick={() => handleDownload()}
            disabled={percentage !== null}
          >
            <span>
              {percentage !== null
                ? `Fetching data... (${percentage.toFixed(0)}%)`
                : 'Download data (csv)'}
            </span>
            <CloudIcon className='icon sm' />
          </button>
        )}
      </div>

      <div className="activity section-box">
        <div className="activity-scroll">
          <table className="activity-table">
            <thead>
              <tr className="header">
                <td className="activity-cell activity-date">Date/Time</td>
                {isMobile ?
                 <td className="activity-cell activity-ip">IP</td>
                 :
                 <td className="activity-cell activity-ip">IP Address</td>
                }
                <td className="activity-cell activity-source">Source</td>
                <td className="activity-cell activity-reason">Reason</td>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => {
                return (
                  <tr key={index}>
                    <td className="activity-cell activity-date">{unixToDate(item['Date/Time (Unix)'])}</td>
                    <td className="activity-cell activity-ip">{item['IP Address']}</td>
                    <td className="activity-cell activity-source">
                      {isMobile
                        ? <span className="activity-text">{item['Source']}</span>
                        : <ScrollingText text={item['Source']} />}
                    </td>
                    <td className="activity-cell activity-reason">
                      {isMobile
                        ? <span className="activity-text">{item['Reason']}</span>
                        : <ScrollingText text={item['Reason']} />}
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && (
                <tr>
                  <td>
                    <span>No data</span>
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default MostRecentActivity;
